import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, MapPin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { useLanguage } from '../contexts/LanguageContext';

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const settings = useSiteSettings();
  const logoUrl = settings.logo_url || '/logo.jpg';
  const { lang, setLang, t, region, regionSlug } = useLanguage();
  const regionPrefix = regionSlug ? `/${regionSlug}` : '';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const withRegion = (path) => {
    if (!regionPrefix) return path;
    return path === '/' ? regionPrefix : `${regionPrefix}${path}`;
  };

  const handleNavClick = (e, path) => {
    // If already on this route, prevent Router navigation and scroll to top.
    if (location.pathname === path) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const exitRegion = () => {
    // Strip the region prefix from the current URL to return to base site.
    const stripped = location.pathname.replace(new RegExp(`^${regionPrefix}`), '') || '/';
    navigate(stripped);
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/mentor', label: 'Our Mentor' },
    { path: '/courses', label: 'Courses' },
    { path: '/events', label: 'Events' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/publications', label: 'Publications' },
    { path: '/contact', label: 'Contact' },
    { path: '/donate', label: 'Donate' },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-surface shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 gap-4">
          <Link to={withRegion('/')} className="flex items-center gap-3 shrink-0" data-testid="logo-link">
            <img
              src={logoUrl}
              alt="Rishidev Foundation"
              className="h-12 w-12 md:h-14 md:w-14 rounded-full object-cover shadow-md"
            />
            <div className="hidden xl:flex items-baseline gap-1 leading-none tracking-tight whitespace-nowrap" style={{ color: '#a37f3f' }}>
              <span className="font-brand text-xl" style={{ fontWeight: 800 }}>Rishidev</span>
              <span className="font-brand text-xl" style={{ fontWeight: 700 }}>Foundation</span>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-5 xl:gap-6 ml-auto">
            {region && (
              <button
                onClick={exitRegion}
                data-testid="region-badge"
                className="flex items-center gap-1.5 bg-saffron-50 border border-saffron-200 text-saffron-700 rounded-full pl-2 pr-3 py-1 text-xs font-body font-semibold hover:bg-saffron-100 transition-colors"
                title="Return to main site"
              >
                <MapPin size={12} />
                <span>{region.display}</span>
                <X size={12} />
              </button>
            )}
            {navLinks.map((link) => {
              const linkPath = withRegion(link.path);
              return (
                <Link
                  key={link.path}
                  to={linkPath}
                  onClick={(e) => handleNavClick(e, linkPath)}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  className={`font-body font-medium text-sm xl:text-base whitespace-nowrap transition-colors hover:text-saffron-600 ${
                    location.pathname === linkPath ? 'text-saffron-600' : 'text-charcoal-700'
                  }`}
                >
                  {t(link.label)}
                </Link>
              );
            })}
            <div
              className="flex items-center bg-charcoal-50 rounded-full p-0.5 text-xs font-body font-semibold"
              data-testid="lang-toggle"
            >
              <button
                onClick={() => setLang('en')}
                data-testid="lang-en"
                className={`px-3 py-1 rounded-full transition-colors ${
                  lang === 'en' ? 'bg-white text-saffron-600 shadow-sm' : 'text-charcoal-500'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLang('hi')}
                data-testid="lang-hi"
                className={`px-3 py-1 rounded-full transition-colors ${
                  lang === 'hi' ? 'bg-white text-saffron-600 shadow-sm' : 'text-charcoal-500'
                }`}
              >
                हिं
              </button>
            </div>
            {user ? (
              <Link
                to={['super_admin', 'state_admin', 'admin'].includes(user.role) ? '/admin' : withRegion('/dashboard')}
                data-testid="dashboard-link"
                className="bg-saffron-600 text-white rounded-full px-4 xl:px-6 py-2 xl:py-2.5 text-sm xl:text-base font-body font-medium hover:bg-saffron-500 transition-colors whitespace-nowrap"
              >
                {t(['super_admin', 'state_admin', 'admin'].includes(user.role) ? 'Admin' : 'Dashboard')}
              </Link>
            ) : (
              <Link
                to={withRegion('/login')}
                data-testid="login-link"
                className="bg-saffron-600 text-white rounded-full px-4 xl:px-6 py-2 xl:py-2.5 text-sm xl:text-base font-body font-medium hover:bg-saffron-500 transition-colors whitespace-nowrap"
              >
                {t('Login')}
              </Link>
            )}
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-charcoal-700 hover:text-saffron-600 transition-colors"
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-charcoal-100" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-4">
            {region && (
              <button
                onClick={exitRegion}
                data-testid="mobile-region-badge"
                className="flex items-center gap-1.5 bg-saffron-50 border border-saffron-200 text-saffron-700 rounded-full pl-2 pr-3 py-1 text-xs font-body font-semibold"
              >
                <MapPin size={12} />
                <span>{region.display}</span>
                <X size={12} />
              </button>
            )}
            {navLinks.map((link) => {
              const linkPath = withRegion(link.path);
              return (
                <Link
                  key={link.path}
                  to={linkPath}
                  onClick={(e) => handleNavClick(e, linkPath)}
                  className="block font-body font-medium text-charcoal-700 hover:text-saffron-600 transition-colors"
                >
                  {t(link.label)}
                </Link>
              );
            })}
            <div className="flex items-center gap-2 pt-2 border-t border-charcoal-100">
              <span className="text-xs uppercase tracking-widest text-charcoal-500 font-semibold">Language</span>
              <button
                onClick={() => setLang('en')}
                data-testid="mobile-lang-en"
                className={`px-3 py-1 rounded-full text-xs font-semibold ${lang === 'en' ? 'bg-saffron-600 text-white' : 'bg-charcoal-100 text-charcoal-600'}`}
              >
                EN
              </button>
              <button
                onClick={() => setLang('hi')}
                data-testid="mobile-lang-hi"
                className={`px-3 py-1 rounded-full text-xs font-semibold ${lang === 'hi' ? 'bg-saffron-600 text-white' : 'bg-charcoal-100 text-charcoal-600'}`}
              >
                हिं
              </button>
            </div>
            {user ? (
              <Link
                to={['super_admin', 'state_admin', 'admin'].includes(user.role) ? '/admin' : withRegion('/dashboard')}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block bg-saffron-600 text-white rounded-full px-6 py-2.5 text-center font-body font-medium hover:bg-saffron-500 transition-colors"
              >
                {['super_admin', 'state_admin', 'admin'].includes(user.role) ? 'Admin' : 'Dashboard'}
              </Link>
            ) : (
              <Link
                to={withRegion('/login')}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block bg-saffron-600 text-white rounded-full px-6 py-2.5 text-center font-body font-medium hover:bg-saffron-500 transition-colors"
              >
                Login
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};